// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useState, useMemo, useCallback, useRef } from 'react';

import { type WorkOrder, useEvolvePaginatedGet, type WorkOrderItem } from '@evolvemep/api-ts';
import { Collapse, Stack } from '@mantine/core';

import { OptionalDetailSection } from 'components/Mantine/DetailPage/OptionalDetailSection';
import { useIsMobile } from 'hooks/useIsMobile';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { BillOfMaterialsModal } from 'modules/Shop/WorkOrders/WorkOrderDetail/BillOfMaterialsModal';
import { getRemainingItemsToShip, getTaskStatusFromItem } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';
import { FabricationItemsModal } from 'modules/Shop/WorkOrders/WorkOrderDetail/FabricationItemsModal';
import { AddItemsToWorkOrderMenu } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/AddItemsToWorkOrderMenu';
import { EditableWorkOrderItem } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/EditableWorkOrderItem';
import { DeleteWorkOrderItem } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/DeleteWorkOrderItem';
import type { WorkOrderItemModalTab } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/WorkOrderItemModal';
import { WorkOrderItemsSectionMenu } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/WorkOrderItemsSectionMenu';
import { AddTasksMenu } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/AddTasksMenu';
import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';

type Props = {
  workOrder: WorkOrder;
  forceWorkOrderRefresh: () => void;
  onItemAttachmentsChanged: () => void;
  onTaskAttachmentsChanged: () => void;
  /** If true, will disable selection of an individual item (which normally opens a Modal) */
  disableSelection?: boolean;
};

export const WorkOrderItemsList = ({
  workOrder,
  forceWorkOrderRefresh,
  onItemAttachmentsChanged,
  onTaskAttachmentsChanged,
  disableSelection,
}: Props) => {
  const isMobile = useIsMobile();
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);

  const {
    fetchNextPage: fetchNextPageWorkOrderItems,
    data: workOrderItems,
    loading: loadingWorkOrderItems,
    entireCount: workOrderItemCount,
    reset: refreshWorkOrderItems,
  } = useEvolvePaginatedGet(`shop/workOrder/item`, {
    defaultConfig: {
      params: {
        workOrderId: workOrder.workOrderId,
      },
    },
  });
  const needsRefresh = useRef(false);

  const [selectedWorkOrderItem, setSelectedWorkOrderItem] = useState<WorkOrderItem | null>(null);
  const onWorkOrderItemSelected = useCallback(
    (item: WorkOrderItem | null) => {
      setSelectedWorkOrderItem(item);
      if (item === null && needsRefresh.current) {
        needsRefresh.current = false;
        refreshWorkOrderItems();
      }
    },
    [refreshWorkOrderItems],
  );

  const selectedWorkOrderItemIndex = useMemo(
    () => workOrderItems.findIndex((item) => item.workOrderItemId === selectedWorkOrderItem?.workOrderItemId),
    [selectedWorkOrderItem?.workOrderItemId, workOrderItems],
  );

  const hasPrev = useMemo(() => selectedWorkOrderItemIndex > 0, [selectedWorkOrderItemIndex]);
  const hasNext = useMemo(
    () => selectedWorkOrderItemIndex >= 0 && selectedWorkOrderItemIndex < workOrderItems.length - 1,
    [selectedWorkOrderItemIndex, workOrderItems.length],
  );
  const onPrev = useCallback(() => {
    onWorkOrderItemSelected(workOrderItems[selectedWorkOrderItemIndex - 1] ?? null);
  }, [selectedWorkOrderItemIndex, workOrderItems, onWorkOrderItemSelected]);
  const onNext = useCallback(() => {
    onWorkOrderItemSelected(workOrderItems[selectedWorkOrderItemIndex + 1] ?? null);
    // If we're nearing the end of the list, load the next page
    if (selectedWorkOrderItemIndex > workOrderItems.length - 5) {
      void fetchNextPageWorkOrderItems();
    }
  }, [fetchNextPageWorkOrderItems, selectedWorkOrderItemIndex, workOrderItems, onWorkOrderItemSelected]);

  const [deletingItem, setDeletingItem] = useState<WorkOrderItem | null>(null);

  const onDeleteWorkOrderItem = useCallback(
    (workOrderItem: WorkOrderItem) => {
      setDeletingItem(workOrderItem);
      onWorkOrderItemSelected(null);
    },
    [onWorkOrderItemSelected],
  );

  const onWorkOrderItemDeleted = useCallback(() => {
    // Deselecting closes the item's modal, which refreshes the list once `needsRefresh` is set
    needsRefresh.current = true;
    onWorkOrderItemSelected(null);
  }, [onWorkOrderItemSelected]);

  const [selectedModalTab, setSelectedModalTab] = useState<WorkOrderItemModalTab>(
    canEditWorkOrder('editTasks') ? 'tasks' : 'bom',
  );

  const allItemsInShippingOrder = useMemo(
    () =>
      workOrderItems.length === workOrderItemCount && workOrderItems.every((woi) => getRemainingItemsToShip(woi) === 0),
    [workOrderItems, workOrderItemCount],
  );

  return (
    <OptionalDetailSection
      title="Items"
      large
      noContentMessage="No items added to work order."
      showNoContentMessage={workOrderItemCount === 0}
      enableDetailSection={!isMobile}
      rightSection={
        <>
          <AddItemsToWorkOrderMenu workOrder={workOrder} onItemsChanged={refreshWorkOrderItems} />
          {
            // These options are not yet mobile-friendly
            !isMobile && (
              <>
                <AddTasksMenu workOrder={workOrder} onTasksUpdated={refreshWorkOrderItems} />
                <FabricationItemsModal workOrder={workOrder} onElementsUpdated={refreshWorkOrderItems} />
                <BillOfMaterialsModal workOrder={workOrder} />
                <WorkOrderItemsSectionMenu
                  workOrder={workOrder}
                  hasWorkOrderItems={workOrderItems.length > 0}
                  onItemsAdded={refreshWorkOrderItems}
                  allItemsInShippingOrder={allItemsInShippingOrder}
                />
              </>
            )
          }
        </>
      }
    >
      <Collapse expanded={workOrderItems.length > 0}>
        <Stack>
          {workOrderItems.map((item) => (
            <EditableWorkOrderItem
              key={item.workOrderItemId}
              item={item}
              workOrder={workOrder}
              onItemUpdated={(
                updatedItem,
                // The internal EditableWorkOrderItem tracks its own updated item
                // that may be newer than out `item` object
                originalItem,
              ) => {
                // Only need to refresh the whole list if it's possible the order changed
                if (updatedItem.name !== originalItem.name) {
                  needsRefresh.current = true;
                }
                // If the status of the item has changed, the status of the WO also may have changed
                if (getTaskStatusFromItem(updatedItem) !== getTaskStatusFromItem(originalItem)) {
                  forceWorkOrderRefresh();
                }
                // If the item's attachment count changed, the page's rollup attachment list is stale
                if (updatedItem.attachmentCount !== originalItem.attachmentCount) {
                  onItemAttachmentsChanged();
                }
              }}
              onMultipleItemsUpdated={refreshWorkOrderItems}
              // If the item's tasks attachments change, the page's rollup attachment list is stale
              onTaskAttachmentsChanged={onTaskAttachmentsChanged}
              onDeleteWorkOrderItem={onDeleteWorkOrderItem}
              onWorkOrderItemDeleted={onWorkOrderItemDeleted}
              selected={selectedWorkOrderItem?.workOrderItemId === item.workOrderItemId}
              onSelectWorkOrderItem={disableSelection ? undefined : onWorkOrderItemSelected}
              navProps={{
                hasPrev,
                hasNext,
                onPrev,
                onNext,
              }}
              selectedModalTab={selectedModalTab}
              onModalTabChange={setSelectedModalTab}
            />
          ))}
        </Stack>
      </Collapse>
      {workOrderItemCount !== 0 && (
        <TriggerOnVisible onVisible={fetchNextPageWorkOrderItems} loading={loadingWorkOrderItems} />
      )}

      <DeleteWorkOrderItem
        workOrderItem={deletingItem}
        onDeleted={() => {
          refreshWorkOrderItems();
          // Deleting an item with attachments also removes them from the page's rollup attachment list
          if ((deletingItem?.attachmentCount ?? 0) > 0) {
            onItemAttachmentsChanged();
          }
        }}
        onClose={() => setDeletingItem(null)}
      />
    </OptionalDetailSection>
  );
};
